import React from 'react';
import { Typography } from 'antd';
import type { BaseFieldProps } from '../../../types/form-renderer';
import type {
  GroupedImageSelectorField as GroupedImageSelectorFieldType,
  GroupItem,
  ItemGroup
} from '../../../types/templates';
import { SLOT_STYLES } from './shared/slot-styles';
import { MediaImage } from './shared/media-image';
import { useImageUpload } from '../../../hooks/media/use-image-upload';
import { UploadTile } from './shared/upload-tile';
import { GroupedSelection } from '../../../types/form/grouped-selection';

const { Text } = Typography;

type SlotIndex = 1 | 2;

// loadSubImages is the resolved registry function (bound to formData); groups
// comes from getDynamicOptions in the renderer.
type Props = BaseFieldProps<GroupedImageSelectorFieldType> & {
  groups: ItemGroup[];
  loadSubImages?: (item: GroupItem, group: ItemGroup) => Promise<string[]>;
};

export const GroupedImageSelectorField: React.FC<Props> = ({
  field,
  groups,
  value,
  onChange,
  commonStyle,
  labelStyle,
  error,
  loadSubImages
}) => {
  const maxPerGroup = field.maxSelectionPerGroup ?? 2;
  const items = React.useMemo<GroupedSelection[]>(() => value ?? [], [value]);
  const [subImagesCache, setSubImagesCache] = React.useState<Record<string, string[]>>({});
  const [loadingItems, setLoadingItems] = React.useState<Record<string, boolean>>({});
  const requested = React.useRef<Set<string>>(new Set());
  const { uploadImage, isUploading } = useImageUpload();

  const selectionsFor = (groupId: string) => items.filter(s => s.groupId === groupId);

  React.useEffect(() => {
    if (!loadSubImages) return;
    items.forEach(sel => {
      if (sel.isCustom) return;
      const group = groups.find(g => g.id === sel.groupId);
      const item = group?.items.find(i => i.value === sel.itemValue);
      if (!group || !item) return;
      if (subImagesCache[item.id] || loadingItems[item.id] || requested.current.has(item.id)) return;

      requested.current.add(item.id);
      setLoadingItems(prev => ({ ...prev, [item.id]: true }));
      loadSubImages(item, group)
        .then(images => setSubImagesCache(prev => ({ ...prev, [item.id]: images })))
        .finally(() => setLoadingItems(prev => ({ ...prev, [item.id]: false })));
    });
  }, [groups, items, loadSubImages, subImagesCache, loadingItems]);

  const handleItemClick = (group: ItemGroup, item: GroupItem) => {
    const isSelected = items.some(s => s.groupId === group.id && s.itemValue === item.value);

    if (isSelected) {
      onChange(items.filter(s => !(s.groupId === group.id && s.itemValue === item.value)));
      return;
    }

    if (selectionsFor(group.id).length >= maxPerGroup) return;

    // Sub-images are hydrated by the effect watching `items`.
    onChange([...items, { groupId: group.id, itemValue: item.value, label: item.label }]);
  };

  const handleSubImageClick = (groupId: string, itemValue: string, subImage: string) => {
    onChange(items.map(s => (s.groupId === groupId && s.itemValue === itemValue ? { ...s, subImage } : s)));
  };

  const handleCustomUpload = async (groupId: string, file: File) => {
    if (selectionsFor(groupId).length >= maxPerGroup) return;
    const url = await uploadImage(file);
    if (url) onChange([...items, { groupId, itemValue: 'custom', label: file.name, subImage: url, isCustom: true }]);
  };

  const handleCustomRemove = (groupId: string, subImage: string) => {
    onChange(items.filter(s => !(s.groupId === groupId && s.subImage === subImage)));
  };

  return (
    <div style={commonStyle}>
      <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
        {field.label}
      </Text>

      {groups.length === 0 && (
        <Text type='secondary' style={{ display: 'block', textAlign: 'center', padding: '16px 0' }}>
          No hay opciones disponibles
        </Text>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
        {groups.map((group, idx) => (
          <GroupBlock
            key={group.id}
            group={group}
            slotIndex={(idx + 1) as SlotIndex}
            selections={selectionsFor(group.id)}
            maxPerGroup={maxPerGroup}
            subImagesCache={subImagesCache}
            loadingItems={loadingItems}
            onItemClick={item => handleItemClick(group, item)}
            onSubImageClick={(itemValue, subImage) => handleSubImageClick(group.id, itemValue, subImage)}
            allowCustomInput={field.allowCustomInput}
            onCustomUpload={handleCustomUpload}
            onCustomRemove={handleCustomRemove}
            isUploading={isUploading}
            field={field}
          />
        ))}
      </div>

      {error && (
        <Text type='danger' style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
          {error}
        </Text>
      )}
    </div>
  );
};

interface GroupBlockProps {
  group: ItemGroup;
  slotIndex: SlotIndex;
  selections: GroupedSelection[];
  maxPerGroup: number;
  subImagesCache: Record<string, string[]>;
  loadingItems: Record<string, boolean>;
  onItemClick: (item: GroupItem) => void;
  onSubImageClick: (itemValue: string, subImage: string) => void;
  allowCustomInput?: boolean;
  onCustomUpload: (groupId: string, file: File) => void;
  onCustomRemove: (groupId: string, subImage: string) => void;
  isUploading?: boolean;
  field: GroupedImageSelectorFieldType;
}

const GroupBlock: React.FC<GroupBlockProps> = ({
  group,
  slotIndex,
  selections,
  maxPerGroup,
  subImagesCache,
  loadingItems,
  onItemClick,
  onSubImageClick,
  allowCustomInput,
  onCustomUpload,
  onCustomRemove,
  isUploading,
  field
}) => {
  const selectedValues = selections.map(s => s.itemValue);
  const isFull = selections.length >= maxPerGroup;
  const slotStyle = SLOT_STYLES[slotIndex];
  const customSelection = selections.filter(s => s.isCustom);

  return (
    <div style={{ border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderBottom: '0.5px solid rgba(0,0,0,0.08)'
        }}
      >
        {group.badgeUrl && (
          <img src={group.badgeUrl} alt={group.label} style={{ width: 22, height: 22, objectFit: 'contain' }} />
        )}
        <Text style={{ fontSize: 12, fontWeight: 500 }}>{group.label}</Text>
        <Text type='secondary' style={{ fontSize: 10, marginLeft: 'auto' }}>
          {selections.length}/{maxPerGroup}
        </Text>
      </div>

      {/* Items - horizontal scroll */}
      <div style={{ display: 'flex', gap: 8, padding: 10, overflowX: 'auto' }}>
        {group.items.map(item => (
          <ItemCard
            key={item.id}
            item={item}
            selIdx={selectedValues.indexOf(item.value)}
            isDisabled={!selectedValues.includes(item.value) && isFull}
            slotStyle={slotStyle}
            onClick={() => onItemClick(item)}
          />
        ))}
        {allowCustomInput && (
          <>
            {customSelection.map(sel => (
              <CustomThumb
                key={sel.subImage}
                url={sel.subImage!}
                onRemove={() => onCustomRemove(group.id, sel.subImage!)}
              />
            ))}
            {!isFull && (
              <UploadTile
                field={field}
                onUpload={file => onCustomUpload(group.id, file)}
                isUploading={isUploading}
                hint='PNG transparente 2048×2048px'
              />
            )}
          </>
        )}
      </div>

      {/* Sub-image strips */}
      {selections.map(sel => {
        const item = group.items.find(i => i.value === sel.itemValue);
        if (!item) return null;
        return (
          <SubImageStrip
            key={item.id}
            item={item}
            images={subImagesCache[item.id]}
            isLoading={!!loadingItems[item.id]}
            selectedImage={sel.subImage}
            slotStyle={slotStyle}
            onSelect={url => onSubImageClick(item.value, url)}
          />
        );
      })}

      {selections.length === 0 && (
        <Text type='secondary' style={{ fontSize: 11, display: 'block', textAlign: 'center', padding: '8px 0 12px' }}>
          Elegí hasta {maxPerGroup}
        </Text>
      )}
    </div>
  );
};

const ItemCard: React.FC<{
  item: GroupItem;
  selIdx: number;
  isDisabled: boolean;
  slotStyle: (typeof SLOT_STYLES)[SlotIndex];
  onClick: () => void;
}> = ({ item, selIdx, isDisabled, slotStyle, onClick }) => {
  const isSelected = selIdx >= 0;
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      onClick={isDisabled ? undefined : onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        flexShrink: 0,
        width: 110,
        border: isSelected
          ? `2px solid ${slotStyle.border}`
          : hovered
            ? '0.5px solid rgba(0,0,0,0.3)'
            : '0.5px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        overflow: 'hidden',
        cursor: isDisabled ? 'default' : 'pointer',
        opacity: isDisabled ? 0.4 : 1,
        background: isSelected ? slotStyle.background : 'var(--color-background-primary)',
        transition: 'border-color 0.12s, background 0.12s'
      }}
    >
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            top: 5,
            right: 5,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: slotStyle.pip.background,
            color: slotStyle.pip.color,
            fontSize: 10,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1
          }}
        >
          {selIdx + 1}
        </div>
      )}

      {item.avatarUrl ? (
        <img
          src={item.avatarUrl}
          alt={item.label}
          style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            aspectRatio: '1',
            background: 'var(--color-background-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Text type='secondary' style={{ fontSize: 11 }}>
            {item.label
              .split(' ')
              .map((w: string) => w[0])
              .join('')
              .substring(0, 2)
              .toUpperCase()}
          </Text>
        </div>
      )}

      <div
        style={{
          padding: '5px 7px',
          borderTop: '0.5px solid rgba(0,0,0,0.08)',
          background: isSelected ? slotStyle.background : 'transparent'
        }}
      >
        <Text
          style={{
            fontSize: 10,
            fontWeight: isSelected ? 500 : 400,
            color: isSelected ? slotStyle.nameColor : undefined,
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {item.label}
        </Text>
      </div>
    </div>
  );
};

const CustomThumb: React.FC<{ url: string; onRemove: () => void }> = ({ url, onRemove }) => (
  <div
    style={{
      position: 'relative',
      flexShrink: 0,
      width: 110,
      borderRadius: 8,
      overflow: 'hidden',
      border: '0.5px solid rgba(0,0,0,0.12)'
    }}
  >
    <MediaImage src={url} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
    <div
      onClick={onRemove}
      style={{
        position: 'absolute',
        top: 4,
        right: 4,
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: 'rgba(0,0,0,0.5)',
        color: 'white',
        fontSize: 11,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      ×
    </div>
  </div>
);

const SubImageStrip: React.FC<{
  item: GroupItem;
  images: string[] | undefined;
  isLoading: boolean;
  selectedImage: string | undefined;
  slotStyle: (typeof SLOT_STYLES)[SlotIndex];
  onSelect: (url: string) => void;
}> = ({ item, images, isLoading, selectedImage, slotStyle, onSelect }) => (
  <div
    style={{
      borderTop: '0.5px solid rgba(0,0,0,0.08)',
      background: 'var(--color-background-secondary)',
      padding: '10px 10px 12px'
    }}
  >
    <Text
      type='secondary'
      style={{ fontSize: 10, letterSpacing: '0.4px', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}
    >
      {item.label}
    </Text>

    {isLoading ? (
      <Text type='secondary' style={{ fontSize: 11 }}>
        Cargando...
      </Text>
    ) : !images?.length ? (
      <Text type='secondary' style={{ fontSize: 11 }}>
        Sin imágenes disponibles
      </Text>
    ) : (
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
        {images.map(url => (
          <div
            key={url}
            onClick={() => onSelect(url)}
            style={{
              flexShrink: 0,
              width: 90,
              height: 130,
              borderRadius: 6,
              border: selectedImage === url ? `2px solid ${slotStyle.border}` : '0.5px solid rgba(0,0,0,0.12)',
              cursor: 'pointer',
              overflow: 'hidden',
              position: 'relative',
              background: 'var(--color-background-primary)',
              transition: 'border-color 0.12s'
            }}
          >
            <MediaImage
              src={url}
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
            />
            {selectedImage === url && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 3,
                  right: 3,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: slotStyle.pip.background,
                  color: 'white',
                  fontSize: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✓
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);
